import { useCallback, useState } from 'react'

import { MinBookingNights } from '../config'

import { isAvailableDateRange } from './is-available-date-range'

export interface IRange {
  startDate?: string
  endDate?: string
}

interface UseCalendarKyrgyzstanProps {
  initialSelectedDate?: IRange
  availableDates?: string[]
  onDateSelect?: (date: IRange) => void
}

const toIsoDate = (date: string): string => {
  const [year, month, day] = date.includes('/')
    ? date.split('/')
    : date.split('-')

  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export const useCalendarKyrgyzstan = ({
  initialSelectedDate,
  availableDates,
  onDateSelect,
}: UseCalendarKyrgyzstanProps) => {
  const [selected, setSelected] = useState<IRange>(initialSelectedDate || {})
  const [activeTab, setActiveTab] = useState<'checkin' | 'checkout' | null>(
    'checkin',
  )

  const handleDateClick = useCallback(
    (calendarDate: string) => {
      const date = toIsoDate(calendarDate)
      let newSelected: IRange

      if (selected.startDate && selected.endDate) {
        newSelected = {
          startDate: date,
          endDate: undefined,
        }
        setActiveTab('checkout')
      } else if (activeTab === 'checkin') {
        newSelected = {
          startDate: date,
          endDate: undefined,
        }
        setActiveTab('checkout')
      } else if (!selected.startDate) {
        newSelected = {
          startDate: date,
          endDate: undefined,
        }
        setActiveTab('checkout')
      } else {
        const startTimestamp = new Date(selected.startDate).getTime()
        const endTimestamp = new Date(date).getTime()

        const daysDiff = (endTimestamp - startTimestamp) / (1000 * 60 * 60 * 24)

        if (daysDiff < MinBookingNights) {
          if (endTimestamp < startTimestamp) {
            newSelected = {
              startDate: date,
              endDate: undefined,
            }
            setActiveTab('checkout')
          } else {
            return
          }
        } else {
          const dateRange = {
            startDate: selected.startDate,
            endDate: date,
          }

          if (!isAvailableDateRange(dateRange, availableDates)) {
            newSelected = { startDate: date, endDate: undefined }
            setActiveTab('checkout')
          } else {
            newSelected = dateRange
          }
        }
      }

      setSelected(newSelected)
      onDateSelect?.(newSelected)

      if (newSelected.startDate && newSelected.endDate) {
        setActiveTab(null)
      }
    },
    [activeTab, selected, availableDates, onDateSelect],
  )

  const handleReset = useCallback(
    (tab: 'checkin' | 'checkout') => {
      const newSelected: IRange =
        tab === 'checkin'
          ? { startDate: undefined, endDate: undefined }
          : { startDate: selected.startDate, endDate: undefined }

      setSelected(newSelected)
      setActiveTab(tab)
      onDateSelect?.(newSelected)
    },
    [selected.startDate, onDateSelect],
  )

  return {
    selected,
    activeTab,
    handleDateClick,
    handleReset,
  }
}
